import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useApp, useScreen } from '@hb/react'
import { useCallback, useMemo, useState } from 'react'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { AssessmentModal } from '../../../Assessments/AssessmentModal/AssessmentModal'
import { AssessmentNote } from '../../../Assessments/AssessmentNote'
import { AssessmentPreview } from '../../../Assessments/AssessmentPreview'
import { PregnancyPlansView } from '../../../Assessments/InsurancePlansView'
import { QuestionnairesPanel } from '../../../Assessments/QuestionnairesPanel'
import { AssessmentPaymentsTab } from '../Payments/AssessmentPayments'
import { AssessmentDesktopTabs } from '../Tabs/AssessmentDesktopTabs'

const ArchivedAssessments = ({ preview }: { preview?: boolean }) => {
  const { assessmentId, goTo, user, assessments } = useProfile()
  const archivedAssessments = useMemo(
    () =>
      Object.entries(assessments ?? {}).filter(
        ([id, val]) => !!val.archivedOn && id !== assessmentId,
      ),
    [assessments, assessmentId],
  )
  return user ? (
    <Accordion allowMultiple defaultIndex={[]} width="100%">
      <AccordionItem>
        <AccordionButton py={1}>
          <Box as="span" flex="1" textAlign="left">
            <Text
              fontWeight={500}
              fontFamily="Open Sans"
              fontSize="md">{`Archived Pregnancies (${archivedAssessments.length})`}</Text>
          </Box>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel px={2} py={0}>
          <Flex w="100%" direction="column" gap={2}>
            {archivedAssessments.map(([id, assessment]) => (
              <AssessmentPreview
                id={id}
                key={id}
                preview={preview}
                buttonText={id === assessmentId ? 'Selected' : 'Select'}
                onSelect={() => goTo({ entity: { type: 'pregnancy', id }, tab: null })}
                user={user}
                assessment={assessment}
              />
            ))}
          </Flex>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  ) : null
}

export const AssessmentsView = ({ preview }: { preview?: boolean }) => {
  const {
    assessments,
    user,
    assessmentId,
    goTo,
    selectedAssessment: { populated: selectedAssessment, assessmentData: selectedAssessmentData },
    admin,
    profileVersion,
  } = useProfile()
  const { appName } = useApp()
  const notSelected = useMemo(() => {
    const entries = Object.entries(assessments ?? {})
    const filtered = preview ? entries : entries.filter(([id]) => id !== assessmentId)
    return filtered.sort(([, a], [, b]) => b.createdOn - a.createdOn)
  }, [assessments, assessmentId, preview])
  const notArchived = useMemo(() => notSelected.filter(([, val]) => !val.archivedOn), [notSelected])

  const [assessmentModalIsOpen, setAssessmentModalIsOpen] = useState(false)

  const onSelect = useCallback(
    (id: string) => {
      switch (profileVersion) {
        case 'v2':
          goTo({ entity: { type: 'pregnancy', id }, tab: 'pregnancy' })
          break
        default:
          if (id === assessmentId) setAssessmentModalIsOpen(true)
          else goTo({ entity: { id, type: 'pregnancy' }, tab: 'pregnancy' })
      }
    },
    [assessmentId, profileVersion, goTo],
  )

  const selectedButtonText = useMemo(
    () => (appName === 'app' ? 'Open' : 'Open Questionnaire'),
    [appName],
  )

  return user ? (
    <VStack align="flex-start" spacing={1} width="100%">
      {assessmentId && !preview ? (
        <Flex gap={2} flexFlow="column" w="100%">
          <Text fontWeight="semibold" color="gray.500">
            Selected pregnancy
          </Text>
          <AssessmentPreview
            id={assessmentId}
            selected
            buttonText={selectedButtonText}
            preview={preview}
            onSelect={() => onSelect(assessmentId)}
            user={user}
            assessment={selectedAssessmentData}
          />
          {preview ? null : <AssessmentNote />}
        </Flex>
      ) : null}
      <Flex px={preview ? 2 : 0} flexFlow="column" w="100%">
        {preview ? null : (
          <Text px={2} fontWeight="semibold" color="gray.500">
            {assessmentId ? 'Other assessments' : 'Assessments'}
          </Text>
        )}
        <Flex w="100%" direction="column" py={preview ? 2 : 0} gap={2}>
          {notArchived.map(([id, assessment]) => (
            <AssessmentPreview
              id={id}
              key={id}
              preview={preview}
              selected={id === assessmentId}
              buttonText="View"
              onSelect={() => onSelect(id)}
              user={user}
              assessment={assessment}
            />
          ))}
        </Flex>
      </Flex>
      <ArchivedAssessments preview={preview} />
      {user && assessmentModalIsOpen ? (
        <AssessmentModal
          assessment={selectedAssessment}
          admin={admin}
          // user={user}
          id={assessmentId}
          isOpen={assessmentModalIsOpen}
          onClose={() => setAssessmentModalIsOpen(false)}
        />
      ) : null}
    </VStack>
  ) : (
    <Text color="red.500">Error: no patient</Text>
  )
}

export const AssessmentsSummary = () => (
  <Box px={4} py={1} w="100%">
    <AssessmentsView />
  </Box>
)

export const AssessmentSummary = ({ width }: { width: number }) => {
  const { appName } = useApp()
  const { isMobile } = useScreen()
  const mobileLayout = useMemo(() => width < 500, [width])
  const { goTo: onTabSelect, subTab, selectedAssessment, subTabs } = useProfile()
  // const [tabIndex, setTabIndex] = useState(0)
  const tabIndex = useMemo(() => {
    if (!subTab) return 0
    const idx = subTabs.findIndex(t => t.path === subTab.path)
    return idx >= 0 ? idx : 0
  }, [subTabs, subTab])
  const onTabChange = useCallback(
    (tabIndex: number) => {
      const selectedTab = subTabs[tabIndex]
      const basePath = appName === 'providers-app' ? 'billing' : 'pregnancy'
      if (selectedTab) {
        onTabSelect({
          entity: { type: 'pregnancy', id: selectedAssessment.id },
          tab: `${basePath}/${selectedTab.path}`,
        })
      }
    },
    [onTabSelect, selectedAssessment, subTabs, appName],
  )

  return (
    <Box w="100%">
      <Tabs onChange={onTabChange} index={tabIndex} size="sm" isLazy variant="soft-rounded">
        <AssessmentDesktopTabs mobileLayout={mobileLayout} tabs={subTabs} />
        <TabPanels>
          {appName === 'app' ? (
            <TabPanel py={isMobile ? 1 : 2} px={isMobile ? 1 : 4}>
              <AssessmentsView />
            </TabPanel>
          ) : (
            <TabPanel py={isMobile ? 1 : 2} px={isMobile ? 1 : 4}>
              <QuestionnairesPanel width={width} mobileLayout={mobileLayout} />
            </TabPanel>
          )}
          {appName === 'app' ? (
            <TabPanel p={isMobile ? 2 : 3}>
              <QuestionnairesPanel width={width} mobileLayout={mobileLayout} />
            </TabPanel>
          ) : (
            <TabPanel>
              <AssessmentsView />
            </TabPanel>
          )}
          {appName === 'providers-app' ? (
            <TabPanel py={0} px={2}>
              <AssessmentPaymentsTab />
            </TabPanel>
          ) : (
            <TabPanel p={3}>
              <Box borderRadius={4} boxShadow="md" bg="white" w="100%">
                <PregnancyPlansView />
              </Box>
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </Box>
  )
}
